import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './StringDiff.module.css';

type Segment = {
  text: string;
  type: 'normal' | 'changed' | 'extra';
};

const splitLines = (text: string): string[] => (text === '' ? [] : text.split(/\r\n|\r|\n/));

const buildCharSegments = (base: string, target: string): Segment[] => {
  if (base === target) {
    return [{ text: target, type: 'normal' }];
  }

  const a = base.split('');
  const b = target.split('');
  const m = a.length;
  const n = b.length;

  // LCS: 先最大化“保留相同字符”，避免把相同片段误标红/黄
  const lcs: number[][] = Array.from({ length: m + 1 }, () => Array(n + 1).fill(0));
  for (let i = m - 1; i >= 0; i -= 1) {
    for (let j = n - 1; j >= 0; j -= 1) {
      if (a[i] === b[j]) {
        lcs[i][j] = lcs[i + 1][j + 1] + 1;
      } else {
        lcs[i][j] = Math.max(lcs[i + 1][j], lcs[i][j + 1]);
      }
    }
  }

  const merged: Segment[] = [];
  const push = (text: string, type: Segment['type']) => {
    if (!text) return;
    const last = merged[merged.length - 1];
    if (last && last.type === type) {
      last.text += text;
    } else {
      merged.push({ text, type });
    }
  };

  let i = 0;
  let j = 0;
  while (i < m || j < n) {
    if (i < m && j < n && a[i] === b[j]) {
      push(b[j], 'normal');
      i += 1;
      j += 1;
      continue;
    }

    const startI = i;
    const startJ = j;
    while (i < m || j < n) {
      if (i < m && j < n && a[i] === b[j]) break;
      if (i < m && (j === n || lcs[i + 1][j] >= lcs[i][j + 1])) {
        i += 1;
      } else if (j < n) {
        j += 1;
      } else {
        i += 1;
      }
    }

    const baseChunkLen = i - startI;
    const targetChunk = b.slice(startJ, j);
    const changedLen = Math.min(baseChunkLen, targetChunk.length);

    if (changedLen > 0) {
      push(targetChunk.slice(0, changedLen).join(''), 'changed');
    }
    if (targetChunk.length > changedLen) {
      push(targetChunk.slice(changedLen).join(''), 'extra');
    }
  }

  return merged.length > 0 ? merged : [{ text: target, type: 'normal' }];
};

const StringDiff: React.FC = () => {
  const { t } = useTranslation();
  const [oldText, setOldText] = useState('');
  const [newText, setNewText] = useState('');
  const oldTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  const newTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  const oldHighlightRef = useRef<HTMLPreElement | null>(null);
  const newHighlightRef = useRef<HTMLPreElement | null>(null);

  const oldLines = useMemo(() => splitLines(oldText), [oldText]);
  const newLines = useMemo(() => splitLines(newText), [newText]);

  const handleClear = () => {
    setOldText('');
    setNewText('');
  };

  const highlightedLines = useMemo(() => {
    const oldHighlighted: Segment[][] = oldLines.map((line) => [{ text: line, type: 'normal' as const }]);
    const newHighlighted: Segment[][] = newLines.map((line) => [{ text: line, type: 'normal' as const }]);
    const maxLineCount = Math.max(oldLines.length, newLines.length);

    for (let i = 0; i < maxLineCount; i += 1) {
      const oldLine = oldLines[i];
      const newLine = newLines[i];

      if (oldLine !== undefined && newLine !== undefined) {
        oldHighlighted[i] = buildCharSegments(newLine, oldLine);
        newHighlighted[i] = buildCharSegments(oldLine, newLine);
      } else if (oldLine !== undefined) {
        oldHighlighted[i] = [{ text: oldLine, type: 'extra' }];
      } else if (newLine !== undefined) {
        newHighlighted[i] = [{ text: newLine, type: 'extra' }];
      }
    }

    return { oldHighlighted, newHighlighted };
  }, [oldLines, newLines]);

  const syncScroll = (
    textareaRef: React.RefObject<HTMLTextAreaElement>,
    highlightRef: React.RefObject<HTMLPreElement>
  ) => {
    const textarea = textareaRef.current;
    const highlight = highlightRef.current;
    if (!textarea || !highlight) return;
    highlight.scrollTop = textarea.scrollTop;
    highlight.scrollLeft = textarea.scrollLeft;
  };

  const renderHighlightedOverlay = (lines: Segment[][]) => {
    if (lines.length === 0) {
      return <span className={styles.overlayPlaceholder}> </span>;
    }

    return lines.map((segments, lineIdx) => (
      <div key={`line-${lineIdx}`} className={styles.overlayLine}>
        {segments.length === 0 ? (
          ' '
        ) : (
          segments.map((segment, idx) => (
            <span
              key={`${lineIdx}-${segment.type}-${idx}`}
              className={
                segment.type === 'extra'
                  ? styles.extraHighlight
                  : segment.type === 'changed'
                    ? styles.changedHighlight
                    : undefined
              }
            >
              {segment.text}
            </span>
          ))
        )}
      </div>
    ));
  };

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <h2 className={styles.title}>{t('stringDiff.title')}</h2>
        <p className={styles.description}>{t('stringDiff.description')}</p>

        <div className={styles.editorGrid}>
          <div>
            <div className={styles.editorLabel}>{t('stringDiff.oldEditorLabel')}</div>
            <div className={styles.textareaWrap}>
              <pre ref={oldHighlightRef} className={styles.highlightLayer} aria-hidden="true">
                {renderHighlightedOverlay(highlightedLines.oldHighlighted)}
              </pre>
              <textarea
                ref={oldTextareaRef}
                value={oldText}
                onChange={(e) => setOldText(e.target.value)}
                onScroll={() => syncScroll(oldTextareaRef, oldHighlightRef)}
                placeholder={t('stringDiff.placeholderOld')}
                className={styles.textarea}
              />
            </div>
          </div>
          <div>
            <div className={styles.editorLabel}>{t('stringDiff.newEditorLabel')}</div>
            <div className={styles.textareaWrap}>
              <pre ref={newHighlightRef} className={styles.highlightLayer} aria-hidden="true">
                {renderHighlightedOverlay(highlightedLines.newHighlighted)}
              </pre>
              <textarea
                ref={newTextareaRef}
                value={newText}
                onChange={(e) => setNewText(e.target.value)}
                onScroll={() => syncScroll(newTextareaRef, newHighlightRef)}
                placeholder={t('stringDiff.placeholderNew')}
                className={styles.textarea}
              />
            </div>
          </div>
        </div>

        <div className={styles.toolbar}>
          <button onClick={handleClear} className={styles.clearButton}>
            {t('stringDiff.clearButton')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default StringDiff;
